import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, KeyRound, LogOut, Loader2 } from "lucide-react";

const RULES = "Au moins 12 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.";

export default function FirstPassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.must_reset) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    if (pw !== pw2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/auth/first-password", { new_password: pw });
      toast.success("Mot de passe mis à jour");
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
      <div className="w-full max-w-md border border-border bg-card rounded-sm p-8" data-testid="first-password-card">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div className="font-head font-extrabold text-lg">CROS</div>
        </div>
        <h1 className="font-head text-xl font-bold mt-4 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" /> Changement de mot de passe requis
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Votre compte utilise un secret temporaire. Définissez un nouveau mot de passe pour accéder à votre espace.
        </p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label className="text-xs">Nouveau mot de passe</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              className="mt-1 rounded-sm" data-testid="new-password-input" />
          </div>
          <div>
            <Label className="text-xs">Confirmer</Label>
            <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              className="mt-1 rounded-sm" data-testid="confirm-password-input" />
          </div>
          <p className="text-[11px] text-muted-foreground">{RULES}</p>
          <Button type="submit" className="w-full rounded-sm" disabled={!pw || !pw2 || busy}
            data-testid="submit-first-password-button">
            {busy ? "Enregistrement…" : "Définir le mot de passe"}
          </Button>
        </form>
        <Button variant="ghost" size="sm" className="w-full mt-3 gap-2 text-muted-foreground"
          onClick={doLogout} data-testid="first-password-logout">
          <LogOut className="h-4 w-4" /> Se déconnecter
        </Button>
      </div>
    </div>
  );
}
